import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { Modal, Button, Select, DropdownItemProps } from "semantic-ui-react";
import { toast } from "../..";
import { connectWithLocalCrewMember, getCrewMembersWithoutCid } from "../../api/crewMembers";
import { formatDate } from "../../models/common";
import { IIDDataSet } from "../../models/crewMember";

type Props = {
    open: boolean;
    closeModal: () => void;
    idDataSet: IIDDataSet;
};

export const ConnectWithLocalModal = ({ open, closeModal, idDataSet }: Props) => {
    const history = useHistory();
    const intl = useIntl();
    const [idDataSets, setIdDataSets] = useState<DropdownItemProps[]>([]);
    const [selected, setSelected] = useState<string>();
    const [loading, setLoading] = useState(false);

    const fetchCrewMembersWithoutCid = useCallback(async () => {
        const props: DropdownItemProps[] = [];
        const res = await getCrewMembersWithoutCid();
        res.forEach(x =>
            props.push({
                key: x.crewMemberGuid,
                text: x.firstName + " " + x.lastName + ` (${formatDate(x.dateOfBirth)})`,
                value: x.crewMemberGuid,
            })
        );
        setIdDataSets(props);
    }, []);

    const handleConnectWithLocalCrewMember = async () => {
        setLoading(true);
        try {
            idDataSet.crewMemberGuid = selected ?? "";
            const targetGuid = await connectWithLocalCrewMember(idDataSet);
            toast(intl.formatMessage({ id: "connectWithLocalCrewMemberSuccess" }), true);
            history.push("/crew-members/" + targetGuid);
        } catch {
            toast(intl.formatMessage({ id: "connectWithLocalCrewMemberFail" }), false);
        } finally {
            setLoading(false);
            setSelected(undefined);
            closeModal();
        }
    };

    useEffect(() => {
        fetchCrewMembersWithoutCid();
    }, [fetchCrewMembersWithoutCid]);

    return (
        <Modal
            open={open}
            onClose={() => closeModal()}
            dimmer="blurring"
            size="tiny"
            closeOnDimmerClick={!loading}
        >
            <Modal.Header>{intl.formatMessage({ id: "connectWithLocalCrewMember" })}</Modal.Header>
            <Modal.Content>
                <section className="input-container">
                    <label>{intl.formatMessage({ id: "selectLocalCrewMember" })}</label>
                    <Select
                        selectOnBlur={false}
                        search
                        options={idDataSets}
                        value={selected}
                        onChange={(e, data: any) => setSelected(data.value)}
                    />
                </section>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    negative
                    onClick={() => {
                        setSelected(undefined);
                        closeModal();
                    }}
                    content={intl.formatMessage({ id: "cancel" })}
                    disabled={loading}
                />
                <Button
                    primary
                    onClick={() => handleConnectWithLocalCrewMember()}
                    content={intl.formatMessage({ id: "confirm" })}
                    loading={loading}
                    disabled={loading || !selected}
                />
            </Modal.Actions>
        </Modal>
    );
};
